import { useEffect, useState } from "react";
import { Modal, Form, Button, Row, Col, Accordion, Alert, Spinner } from "react-bootstrap";
import { FaTimes, FaPlus, FaSave, FaPlay, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { APITool, ParameterConfig } from "../../models/agent";


interface ApiToolModalProps {
    show: boolean;
    apiTool: APITool | null;
    onHide: () => void;
    onSave: (tool: Omit<APITool, "id" | "createdAt" | "updatedAt">) => void;
    onUpdate: (tool: APITool) => void;
}

function ApiToolModal({
    show,
    apiTool,
    onHide,
    onSave,
    onUpdate,
}: ApiToolModalProps) {
    const [name, setName] = useState<string>(apiTool?.name || "");
    const [description, setDescription] = useState<string>(apiTool?.description || "");
    const [method, setMethod] = useState<string>(apiTool?.method || "GET");
    const [url, setUrl] = useState<string>(apiTool?.url || "");
    const [headers, setHeaders] = useState<Record<string, ParameterConfig>>(apiTool?.headers || {});
    const [params, setParams] = useState<Record<string, ParameterConfig>>(apiTool?.params || {});
    const [data, setData] = useState<Record<string, ParameterConfig>>({});
    const [enabled, setEnabled] = useState<boolean>(apiTool?.enabled || true);

    const [isTestingApi, setIsTestingApi] = useState(false);
    const [testResult, setTestResult] = useState<{success: boolean;status?: number;data?: any;error?: string;} | null>(null);
    // const [newTool, setNewTool] = useState<Omit<APITool, "id"> | null>(null);
    // console.log("ApiToolModal - apiTool:", apiTool);

    const testApiRequest = async () => {
        setIsTestingApi(true);
        setTestResult(null);

        if(!url || url.trim() === "") {
            setTestResult({
                success: false,
                error: "API URL is required for testing."
            });
            setIsTestingApi(false);
            return;
        }

        try {
            const config: RequestInit = {
                method: method,
                headers: {},
            };

            // Add headers
            if (headers) {
                Object.entries(headers).forEach(([key, paramConfig]) => {
                    const paramConf = paramConfig as ParameterConfig;
                    if (paramConf.value) {
                        (config.headers as Record<string, string>)[key] = paramConf.value;
                    }
                });
            }

            // Build URL with query parameters
            let searchUrl = url;
            if (params && Object.keys(params).length > 0) {
                const searchParams = new URLSearchParams();
                Object.entries(params).forEach(([key, paramConfig]) => {
                    const paramConf = paramConfig as ParameterConfig;
                    if (paramConf.value) {
                        searchParams.append(key, paramConf.value);
                    }
                });
                if (searchParams.toString()) {
                    searchUrl += '?' + searchParams.toString();
                }
            }

            // Add request body for POST, PUT, PATCH
            const hasRequestBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
            if (hasRequestBody && data && Object.keys(data).length > 0) {
                const bodyData: Record<string, any> = {};
                Object.entries(data).forEach(([key, paramConfig]) => {
                    const paramConf = paramConfig as ParameterConfig;
                    if (paramConf.value) {
                        bodyData[key] = paramConf.value;
                    }
                });
                
                if (Object.keys(bodyData).length > 0) {
                    config.body = JSON.stringify(bodyData);
                    (config.headers as Record<string, string>)['Content-Type'] = 'application/json';
                }
            }

            const response = await fetch(searchUrl, config);
            const responseData = await response.text();
            
            let parsedData;
            try {
                parsedData = JSON.parse(responseData);
            } catch {
                parsedData = responseData;
            }

            setTestResult({
                success: response.ok,
                status: response.status,
                data: parsedData
            });

        } catch (error) {
            setTestResult({
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            });
        } finally {
            setIsTestingApi(false);
        }
    };

    const addNewParam = (field: "headers" | "params" | "data") => {
        const addConfig = (config: Record<string, ParameterConfig>) => {
            return {
                ...config,
                "": {
                    value: "",
                    required: false,
                    type: "user-input" as 'user-input' | 'system-value',
                    description: "",
                },
            };
        };

        if (field === "headers") {
            setHeaders(addConfig(headers));
        } else if (field === "params") {
            setParams(addConfig(params));
        } else if (field === "data") {
            setData(addConfig(data));
        }
    };

    const updateParamKey = (field: "headers" | "params" | "data", oldKey: string, newKey: string, config: ParameterConfig) => {
        const updateConfig = (obj: Record<string, ParameterConfig>) => {
            const newObj = { ...obj };
            if (oldKey !== newKey) {
                delete newObj[oldKey];
            }
            newObj[newKey] = config;
            return newObj;
        };

        if (field === "headers") {
            setHeaders(updateConfig(headers));
        } else if (field === "params") {
            setParams(updateConfig(params));
        } else if (field === "data") {
            setData(updateConfig(data));
        }
    };

    const updateParamValue = (field: "headers" | "params" | "data", key: string, config: ParameterConfig) => {
        const updateConfig = (obj: Record<string, ParameterConfig>) => {
            return {
                ...obj,
                [key]: config,
            };
        };

        if (field === "headers") {
            setHeaders(updateConfig(headers));
        } else if (field === "params") {
            setParams(updateConfig(params));
        } else if (field === "data") {
            setData(updateConfig(data));
        }
    };

    const removeParam = (field: "headers" | "params" | "data", key: string) => {
        const removeConfig = (obj: Record<string, ParameterConfig>) => {
            const newObj = { ...obj };
            delete newObj[key];
            return newObj;
        };

        if (field === "headers") {
            setHeaders(removeConfig(headers));
        } else if (field === "params") {
            setParams(removeConfig(params));
        } else if (field === "data") {
            setData(removeConfig(data));
        }
    };

    const handleSubmit = () => {
        const toolData = {
            name,
            description,
            method,
            url,
            headers,
            params,
            data,
            enabled,
        };

        if (apiTool) {
            onUpdate({ ...apiTool, ...toolData });
        } else {
            onSave(toolData);
        }
    }

    useEffect(() => {
        if (apiTool) {
            setName(apiTool.name);
            setDescription(apiTool.description);
            setMethod(apiTool.method);
            setUrl(apiTool.url);
            setHeaders(apiTool.headers || {});
            setParams(apiTool.params || {});
            setData(apiTool.data || {});
            // setResponseType(apiTool.responseType || "json");
            setEnabled(apiTool.enabled || true);
        } else {
            setName("");
            setDescription("");
            setMethod("GET");
            setUrl("");
            setHeaders({
                "Content-Type": {
                    value: "application/json",
                    required: true,
                    type: "system-value",
                    description: "Default content type for JSON requests",
                },
            });
            setParams({});
            setData({});
            setEnabled(true);
        }
    }, [apiTool]);

    const renderKeyValueSection = (
        field: "headers" | "params" | "data",
        title: string,
    ) => {
        return (
        <Accordion.Item
            eventKey={field}
            style={{ backgroundColor: "#1a1a1a", border: "1px solid #555" }}
        >
            <Accordion.Header style={{ backgroundColor: "#1a1a1a" }}>
                <span className="text-light">{title} (Optional)</span>
            </Accordion.Header>
            <Accordion.Body style={{ backgroundColor: "#1a1a1a" }}>
                {Object.entries(field === 'headers' ? headers : field === 'params' ? params : data).map(
                    ([key, config], index) => {
                        const paramConfig = config as ParameterConfig;
                        return (
                            <Row key={index} className="mb-3">
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Key"
                                        value={key}
                                        onChange={e => updateParamKey(field, key, e.target.value, paramConfig)}
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Value"
                                        value={paramConfig.value}
                                        onChange={e => updateParamValue(field, key, {...paramConfig, value: e.target.value})}
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        value={paramConfig.type}
                                        onChange={e => updateParamValue(field, key, {...paramConfig, type: e.target.value as 'user-input' | 'system-value'})}
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    >
                                        <option value="user-input">
                                            User Input
                                        </option>
                                        <option value="system-value">
                                            System Value
                                        </option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Check
                                        type="switch"
                                        label="Required"
                                        checked={paramConfig.required}
                                        onChange={e => updateParamValue(field, key, {...paramConfig, required: e.target.checked})}
                                        style={{ color: "#fff" }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeParam(field, key)}
                                    >
                                        <FaTimes />
                                    </Button>
                                </Col>
                                <Col md={12} className="mt-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Description (optional)"
                                        value={paramConfig.description || ""}
                                        onChange={e => updateParamValue(field, key, {...paramConfig, description: e.target.value})}
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    />
                                </Col>
                            </Row>
                        );
                    },
                )}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => addNewParam(field)}
                >
                    <FaPlus className="me-1" />
                    Add {title.slice(0, -1)}
                </Button>
            </Accordion.Body>
        </Accordion.Item>
    );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            contentClassName="bg-dark text-light"
        >
            <Modal.Header
                closeButton
                closeVariant="white"
                style={{
                    backgroundColor: "#2d2d2d",
                    borderBottom: "1px solid #444",
                }}
            >
                <Modal.Title>
                    {apiTool ? "Edit API Tool" : "Add New API Tool"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                style={{
                    backgroundColor: "#2d2d2d",
                    maxHeight: "70vh",
                    overflowY: "auto",
                }}
            >
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-light">
                                    Tool Name
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="e.g., Get Order Status"
                                    style={{
                                        backgroundColor: "#1a1a1a",
                                        borderColor: "#555",
                                        color: "#fff",
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-light">
                                    HTTP Method
                                </Form.Label>
                                <Form.Select
                                    value={method}
                                    onChange={(e) => setMethod(e.target.value)}
                                    style={{
                                        backgroundColor: "#1a1a1a",
                                        borderColor: "#555",
                                        color: "#fff",
                                    }}
                                >
                                    <option value="GET">GET</option>
                                    <option value="POST">POST</option>
                                    <option value="PUT">PUT</option>
                                    <option value="DELETE">DELETE</option>
                                    <option value="PATCH">PATCH</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-light">API URL</Form.Label>
                        <Form.Control
                            type="text"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://api.example.com/orders/status"
                            style={{
                                backgroundColor: "#1a1a1a",
                                borderColor: "#555",
                                color: "#fff",
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-light">
                            Description
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what this tool does..."
                            style={{
                                backgroundColor: "#1a1a1a",
                                borderColor: "#555",
                                color: "#fff",
                            }}
                        />
                    </Form.Group>

                    {/* Parameter Configuration Sections */}
                    <Accordion className="mb-3">
                        {renderKeyValueSection('headers', "Headers")}
                        {renderKeyValueSection('params', "Parameters")}
                        {/* Hide request body section for GET and DELETE methods */}
                        {method !== 'GET' && method !== 'DELETE' && renderKeyValueSection('data', "Request Body")}
                    </Accordion>

                    {/* Test API Section */}
                    {/* {newTool.url && ( */}
                    <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="text-light mb-0">Test API Request</h6>
                            <Button 
                                variant="outline-info"
                                size="sm"
                                onClick={testApiRequest}
                                disabled={isTestingApi || (!apiTool?.id && !url.trim())}
                            >
                                {isTestingApi ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Testing...
                                    </>
                                ) : (
                                    <>
                                        <FaPlay className="me-2" />
                                        Test Request
                                    </>
                                )}
                            </Button>
                        </div>
                        {testResult && (
                            <Alert 
                                variant={testResult?.success ? "success" : "danger"}
                                className="mt-2"
                            >
                                <div className="d-flex align-items-center mb-2">
                                    {testResult?.success ? (
                                        <FaCheckCircle className="me-2" />
                                    ) : (
                                        <FaExclamationTriangle className="me-2" />
                                    )}
                                    <strong>
                                        {testResult?.success ? 'Success' : 'Error'}
                                        {testResult?.status && ` (${testResult.status})`}
                                    </strong>
                                </div>
                                
                                {testResult?.error && (
                                    <div className="mb-2">
                                        <small>{testResult.error}</small>
                                    </div>
                                )}
                                
                                {testResult?.data && (
                                    <details>
                                        <summary style={{ cursor: 'pointer' }}>
                                            <small>View Response</small>
                                        </summary>
                                        <pre 
                                            style={{ 
                                                fontSize: '12px', 
                                                maxHeight: '200px', 
                                                overflow: 'auto',
                                                backgroundColor: '#f8f9fa',
                                                padding: '10px',
                                                borderRadius: '4px',
                                                marginTop: '10px'
                                            }}
                                        >
                                            {JSON.stringify(testResult.data, null, 2)}
                                        </pre>
                                    </details>
                                )}
                            </Alert>
                        )}
                    </div>
                    {/* )} */}
                </Form>
            </Modal.Body>
            <Modal.Footer
                style={{
                    backgroundColor: "#2d2d2d",
                    borderTop: "1px solid #444",
                }}
            >
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!apiTool && !url.trim()}
                >
                    <FaSave className="me-1" />
                    {apiTool ? "Update Tool" : "Add Tool"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ApiToolModal;
